// Section 2: Existing Areas & Property Details
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Home } from "lucide-react";
import { IndianNumberInput } from "@/components/IndianNumberInput";

export const ExistingAreasSection = ({
  formData,
  setFormData,
  handleChange,
  handleSelectChange,
  handleCheckboxChange
}) => {
  // Only render for redevelopment projects
  if (formData.projectMode === "greenfield") {
    return null;
  }

  return (
    <Card className="rounded-sm shadow-sm border-l-4 border-l-emerald-400" data-testid="quick-existing-areas">
      <CardHeader className="pb-3 bg-emerald-100 rounded-t-xl border-b border-emerald-200">
        <CardTitle className="font-heading tracking-tight text-lg flex items-center gap-2 text-emerald-900">
          <Home className="h-5 w-5 text-muted-foreground" />
          2. Existing Areas & Property Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Property Types Checkboxes */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="q-hasFlats"
              checked={formData.hasFlats}
              onCheckedChange={(checked) => handleCheckboxChange("hasFlats", checked)}
              data-testid="quick-has-flats"
            />
            <Label htmlFor="q-hasFlats" className="cursor-pointer">Flats</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="q-hasOffices"
              checked={formData.hasOffices}
              onCheckedChange={(checked) => handleCheckboxChange("hasOffices", checked)}
              data-testid="quick-has-offices"
            />
            <Label htmlFor="q-hasOffices" className="cursor-pointer">Offices</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="q-hasShops"
              checked={formData.hasShops}
              onCheckedChange={(checked) => handleCheckboxChange("hasShops", checked)}
              data-testid="quick-has-shops"
            />
            <Label htmlFor="q-hasShops" className="cursor-pointer">Shops</Label>
          </div>
        </div>
        
        {/* Area Inputs */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {formData.hasFlats && (
            <>
              <div className="space-y-2">
                <Label htmlFor="q-areaFlats">Flats Area (sft)</Label>
                <IndianNumberInput
                  id="q-areaFlats"
                  name="areaFlats"
                  value={formData.areaFlats}
                  onChange={handleChange}
                  placeholder="10,000"
                  className="rounded-sm"
                  data-testid="quick-area-flats"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-freeAreaPercentFlats">Free Area - Flats (%)</Label>
                <Input
                  id="q-freeAreaPercentFlats"
                  name="freeAreaPercentFlats"
                  type="number"
                  value={formData.freeAreaPercentFlats}
                  onChange={handleChange}
                  placeholder="10"
                  className="rounded-sm"
                  data-testid="quick-free-area-flats"
                />
              </div>
            </>
          )}
          {formData.hasOffices && (
            <>
              <div className="space-y-2">
                <Label htmlFor="q-areaOffices">Offices Area (sft)</Label>
                <IndianNumberInput
                  id="q-areaOffices"
                  name="areaOffices"
                  value={formData.areaOffices}
                  onChange={handleChange}
                  placeholder="2,000"
                  className="rounded-sm"
                  data-testid="quick-area-offices"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-freeAreaPercentOffices">Free Area - Offices (%)</Label>
                <Input
                  id="q-freeAreaPercentOffices"
                  name="freeAreaPercentOffices"
                  type="number"
                  value={formData.freeAreaPercentOffices}
                  onChange={handleChange}
                  placeholder="10"
                  className="rounded-sm"
                  data-testid="quick-free-area-offices"
                />
              </div>
            </>
          )}
          {formData.hasShops && (
            <>
              <div className="space-y-2">
                <Label htmlFor="q-areaShops">Shops Area (sft)</Label>
                <IndianNumberInput
                  id="q-areaShops"
                  name="areaShops"
                  value={formData.areaShops}
                  onChange={handleChange}
                  placeholder="1,000"
                  className="rounded-sm"
                  data-testid="quick-area-shops"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="q-freeAreaPercentShops">Free Area - Shops (%)</Label>
                <Input
                  id="q-freeAreaPercentShops"
                  name="freeAreaPercentShops"
                  type="number"
                  value={formData.freeAreaPercentShops}
                  onChange={handleChange}
                  placeholder="10"
                  className="rounded-sm"
                  data-testid="quick-free-area-shops"
                />
              </div>
            </>
          )}
        </div>

        {/* Area Offer Mode: Percentage vs Direct */}
        {formData.projectMode !== "greenfield" && (
          <div className="space-y-3 p-3 bg-blue-50/50 border border-blue-200 rounded-sm">
            <div className="flex items-center gap-4">
              <Label className="font-medium text-sm">Area Offer Method:</Label>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="radio" name="areaOfferMode" value="percentage"
                    checked={formData.areaOfferMode !== "direct"}
                    onChange={() => setFormData(prev => ({ ...prev, areaOfferMode: "percentage" }))}
                    className="h-3.5 w-3.5" data-testid="area-offer-mode-pct" />
                  Free Area %
                </label>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                  <input type="radio" name="areaOfferMode" value="direct"
                    checked={formData.areaOfferMode === "direct"}
                    onChange={() => setFormData(prev => ({ ...prev, areaOfferMode: "direct" }))}
                    className="h-3.5 w-3.5" data-testid="area-offer-mode-direct" />
                  Direct Total Area (sft)
                </label>
              </div>
            </div>
            {formData.areaOfferMode === "direct" && (
              <div className="space-y-2">
                <div className="grid md:grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Total Area Offered to Society (sft)</Label>
                    <IndianNumberInput
                      name="directAreaOfferedSqft"
                      value={formData.directAreaOfferedSqft || ""}
                      onChange={handleChange}
                      placeholder="e.g. 15,000"
                      className="rounded-sm h-8 text-xs"
                      data-testid="direct-area-offered"
                    />
                  </div>
                  {(() => {
                    const existingSqft = (parseFloat(formData.areaFlats) || 0) + (parseFloat(formData.areaOffices) || 0) + (parseFloat(formData.areaShops) || 0);
                    const offeredSqft = parseFloat(formData.directAreaOfferedSqft) || 0;
                    const backCalcPct = existingSqft > 0 ? ((offeredSqft - existingSqft) / existingSqft * 100) : 0;
                    const offeredSqm = offeredSqft / 10.76;
                    return (
                      <>
                        <div className="p-2 bg-white rounded-sm text-xs text-center">
                          <p className="text-muted-foreground">Back-calculated Free %</p>
                          <p className="font-mono font-semibold text-blue-700">{backCalcPct > 0 ? backCalcPct.toFixed(1) : '0'}%</p>
                        </div>
                        <div className="p-2 bg-white rounded-sm text-xs text-center">
                          <p className="text-muted-foreground">Total Area (sq mtr)</p>
                          <p className="font-mono font-semibold">{offeredSqm.toFixed(2)} sq mtr</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <p className="text-[10px] text-muted-foreground">When all flats are same size, enter the total area being offered. Free % and stamp duty will be calculated from this figure. (1 sq mtr = 10.76 sq ft)</p>
              </div>
            )}
          </div>
        )}

        <Separator />

        {/* Terrace & Garden Toggle */}
        <div className="space-y-4">
          <div className="space-y-2 max-w-xs">
            <Label>Is there existing Terrace/Garden Area? *</Label>
            <Select
              value={formData.hasTerraceGarden || ""}
              onValueChange={(value) => {
                const newData = { ...formData, hasTerraceGarden: value };
                if (value === "no") {
                  newData.areaTerrace = "0";
                  newData.areaOverheadTerrace = "0";
                  newData.areaGarden = "0";
                  newData.terraceConversionRatio = "0";
                  newData.overheadTerraceConversionRatio = "0";
                  newData.gardenConversionRatio = "0";
                }
                setFormData(newData);
              }}
            >
              <SelectTrigger className="rounded-sm" data-testid="quick-has-terrace-garden">
                <SelectValue placeholder="Select Yes / No" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {formData.hasTerraceGarden === "yes" && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="q-areaTerrace">Terrace Area - Adjacent (sft)</Label>
                <Input
                  id="q-areaTerrace"
                  name="areaTerrace"
                  type="number"
                  value={formData.areaTerrace}
                  onChange={handleChange}
                  placeholder="0"
                  className="rounded-sm"
                  data-testid="quick-terrace-area"
                />
              </div>
              {parseFloat(formData.areaTerrace) > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="q-terraceConversionRatio">Adjacent Terrace Conversion (%)</Label>
                  <Input
                    id="q-terraceConversionRatio"
                    name="terraceConversionRatio"
                    type="number"
                    value={formData.terraceConversionRatio}
                    onChange={handleChange}
                    placeholder="25"
                    className="rounded-sm"
                    data-testid="quick-terrace-ratio"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="q-areaOverheadTerrace">Terrace Area - Overhead (sft)</Label>
                <Input
                  id="q-areaOverheadTerrace"
                  name="areaOverheadTerrace"
                  type="number"
                  value={formData.areaOverheadTerrace}
                  onChange={handleChange}
                  placeholder="0"
                  className="rounded-sm"
                  data-testid="quick-overhead-terrace-area"
                />
              </div>
              {parseFloat(formData.areaOverheadTerrace) > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="q-overheadTerraceConversionRatio">Overhead Terrace Conversion (%)</Label>
                  <Input
                    id="q-overheadTerraceConversionRatio"
                    name="overheadTerraceConversionRatio"
                    type="number"
                    value={formData.overheadTerraceConversionRatio}
                    onChange={handleChange}
                    placeholder="25"
                    className="rounded-sm"
                    data-testid="quick-overhead-terrace-ratio"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="q-areaGarden">Garden Area (sft)</Label>
                <Input
                  id="q-areaGarden"
                  name="areaGarden"
                  type="number"
                  value={formData.areaGarden}
                  onChange={handleChange}
                  placeholder="0"
                  className="rounded-sm"
                  data-testid="quick-garden-area"
                />
              </div>
              {parseFloat(formData.areaGarden) > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="q-gardenConversionRatio">Garden Conversion (%)</Label>
                  <Input
                    id="q-gardenConversionRatio"
                    name="gardenConversionRatio"
                    type="number"
                    value={formData.gardenConversionRatio}
                    onChange={handleChange}
                    placeholder="25"
                    className="rounded-sm"
                    data-testid="quick-garden-ratio"
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Apply increment toggle */}
        {(parseFloat(formData.areaTerrace) > 0 || parseFloat(formData.areaGarden) > 0) && 
         (parseFloat(formData.additionalFreeAreaPercent) > 0 || parseFloat(formData.freeAreaPercentFlats) > 0) && (
          <div className="space-y-2 bg-accent/5 p-3 rounded-sm border border-accent/20">
            <Label className="text-sm font-medium">
              Apply {formData.freeAreaPercentFlats || formData.additionalFreeAreaPercent || 0}% increment on converted terrace/garden area?
            </Label>
            <Select
              value={formData.applyIncrementOnTerraceGarden}
              onValueChange={(value) => setFormData({...formData, applyIncrementOnTerraceGarden: value})}
            >
              <SelectTrigger className="rounded-sm max-w-xs" data-testid="quick-increment-terrace-garden-toggle">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              If yes, the additional free area % will also apply on converted terrace &amp; garden area
            </p>
          </div>
        )}

        <Separator />

        {/* Existing Parkings */}
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Is there existing allotted parking? *</Label>
              <Select
                value={formData.hasExistingParking || ""}
                onValueChange={(value) => {
                  const newData = { ...formData, hasExistingParking: value };
                  if (value === "no") {
                    newData.existingParkings = "0";
                    newData.parkingCompensationType = "none";
                  }
                  setFormData(newData);
                }}
              >
                <SelectTrigger className="rounded-sm" data-testid="quick-has-existing-parking">
                  <SelectValue placeholder="Select Yes / No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.hasExistingParking === "yes" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="q-existingParkings">Number of Existing Parkings</Label>
                  <Input
                    id="q-existingParkings"
                    name="existingParkings"
                    type="number"
                    value={formData.existingParkings}
                    onChange={handleChange}
                    placeholder="10"
                    className="rounded-sm"
                    data-testid="quick-existing-parkings"
                  />
                </div>
                {parseInt(formData.existingParkings) > 0 && (
                  <>
                    <div className="space-y-2">
                      <Label>Parking Compensation</Label>
                      <Select
                        value={formData.parkingCompensationType}
                        onValueChange={(value) => handleSelectChange("parkingCompensationType", value)}
                      >
                        <SelectTrigger className="rounded-sm" data-testid="quick-parking-comp-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="parking">Additional Parking</SelectItem>
                          <SelectItem value="carpet">Additional Carpet Area</SelectItem>
                          <SelectItem value="payout">Monetary Payout</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {formData.parkingCompensationType === "carpet" && (
                      <div className="space-y-2">
                        <Label htmlFor="q-compensationCarpetArea">Carpet Area (sft)</Label>
                        <Input
                          id="q-compensationCarpetArea"
                          name="compensationCarpetArea"
                          type="number"
                          value={formData.compensationCarpetArea}
                          onChange={handleChange}
                          placeholder="500"
                          className="rounded-sm"
                          data-testid="quick-comp-carpet"
                        />
                      </div>
                    )}
                    {formData.parkingCompensationType === "payout" && (
                      <div className="space-y-2">
                        <Label htmlFor="q-compensationPayout">Payout Amount per Parking (₹)</Label>
                        <IndianNumberInput
                          id="q-compensationPayout"
                          name="compensationPayout"
                          value={formData.compensationPayout}
                          onChange={handleChange}
                          placeholder="5,00,000"
                          className="rounded-sm"
                          data-testid="quick-comp-payout"
                        />
                        {parseFloat(formData.compensationPayout) > 0 && parseInt(formData.existingParkings) > 0 && (
                          <p className="text-xs text-amber-700 bg-amber-50 p-1.5 rounded">
                            Total Payout: {parseInt(formData.existingParkings)} parkings × ₹{parseInt(formData.compensationPayout).toLocaleString('en-IN')} = <strong>₹{(parseInt(formData.existingParkings) * parseInt(formData.compensationPayout)).toLocaleString('en-IN')}</strong>
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          
            {/* Old Road Widening */}
            <div className="space-y-3">
              <Label className="font-medium">Old Road Widening (from Old Sanction)?</Label>
              <p className="text-xs text-muted-foreground -mt-1">Reduces TDR purchase at 1:1 ratio.</p>
              <RadioGroup 
                value={formData.hasOldRoadWidening || "no"} 
                onValueChange={(value) => {
                  setFormData({
                    ...formData, 
                    hasOldRoadWidening: value,
                    oldRoadWideningArea: value === "no" ? "0" : formData.oldRoadWideningArea
                  });
                }}
                className="flex gap-4"
                data-testid="quick-old-road-widening-radio"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="yes" id="q-old-road-widening-yes" data-testid="quick-old-road-widening-yes" />
                  <Label htmlFor="q-old-road-widening-yes" className="font-normal cursor-pointer">Yes</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="no" id="q-old-road-widening-no" data-testid="quick-old-road-widening-no" />
                  <Label htmlFor="q-old-road-widening-no" className="font-normal cursor-pointer">No</Label>
                </div>
              </RadioGroup>
              
              {formData.hasOldRoadWidening === "yes" && (
                <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                  <Label htmlFor="q-oldRoadWideningArea">Old Road Widening Area (sq mtr)</Label>
                  <Input
                    id="q-oldRoadWideningArea"
                    name="oldRoadWideningArea"
                    type="number"
                    value={formData.oldRoadWideningArea}
                    onChange={handleChange}
                    placeholder="0"
                    className="rounded-sm"
                    data-testid="quick-old-road-widening"
                  />
                  <p className="text-xs text-muted-foreground">TDR reduction = Area × 1</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
